/**
 * Iris Review Queue — human QA over the AI service agent's conversations.
 * Reviewers grade Iris' handling (pass/fail + a 1-5 score), leave a failure
 * note and a suggested fix, and track each conversation through a review
 * lifecycle (Not reviewed → Reviewed / Needs fix → Fix complete).
 *
 * Reached through the operations proxy at `/api/messaging/iris/reviews`
 * (operations is the FE's auth boundary + RBAC gate; it forwards to
 * ai-operations with the shared service key), mirroring the sibling
 * `receptionists.ts` insights/conversations hooks.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';
import type { IrisOutcome } from '@/lib/ai/receptionists';

const BASE = '/api/messaging/iris/reviews';

/** Review lifecycle of a conversation in the QA queue. */
export type ReviewStatus =
  /** Sampled/queued but no human has graded it yet. */
  | 'NOT_REVIEWED'
  /** Graded and Iris passed — nothing to fix. */
  | 'REVIEWED'
  /** Graded as a failure — needs a fix (prompt / knowledge / flow change). */
  | 'NEEDS_FIX'
  /** The flagged failure has been addressed. */
  | 'FIX_COMPLETE';

/** Reviewer verdict on how Iris handled the conversation. */
export type ReviewVerdict = 'PASS' | 'FAIL';

/** One conversation in the review queue — the conversation summary plus the
 * reviewer's grade (present once someone has reviewed it). */
export interface IrisReviewRow {
  /** The reviewed SmsConversation id. */
  conversationId: string;
  status: ReviewStatus;
  /** Conversation summary (mirrors IrisConversationRow) for the list card. */
  aiTitle: string | null;
  outcome: IrisOutcome;
  cxScore: number | null;
  customerPhone: string;
  topic: string | null;
  lastActivity: string;
  /** Review grade — null until a human grades it. */
  verdict: ReviewVerdict | null;
  /**
   * The reviewer's quality score. The scale depends on how it was graded: a
   * manual grade stores 1-5, a scorecard grade stores the server-computed
   * weighted percentage (0-100). Read it with {@link scorecardId} to know which.
   */
  score: number | null;
  /** The scorecard this review was graded against, if any. */
  scorecardId: string | null;
  /** Per-criterion grades keyed by criterion name (0 fail / 50 partial / 100 pass). */
  criterionScores: Record<string, number> | null;
  /** What went wrong (present on failures). */
  failureNote: string | null;
  /** Proposed fix (prompt/knowledge/flow tweak). */
  suggestedFix: string | null;
  /** Who last reviewed it, and when. */
  reviewedBy: string | null;
  reviewedAt: string | null;
}

export interface IrisReviewList {
  items: IrisReviewRow[];
  nextCursor: string | null;
}

/** Counts per lifecycle bucket, powering the filter-chip badges. */
export type ReviewCounts = Record<ReviewStatus, number>;

/** Payload to create/update a review grade for a conversation. */
export interface UpsertReviewPayload {
  conversationId: string;
  status: ReviewStatus;
  verdict?: ReviewVerdict;
  /** 1-5 manual grade; `null` clears a stale score when detaching a rubric. */
  score?: number | null;
  failureNote?: string;
  suggestedFix?: string;
  /**
   * Grade against a scorecard. When sent together with {@link criterionScores}
   * the backend computes the weighted score and pass/fail from the rubric and
   * those win over any flat `score`/`verdict` also sent. Send `null` to detach
   * a previously scorecard-graded review — the backend then clears the rubric
   * link and its grades, so a flat 1-5 `score` can't be mistaken for a weighted
   * percentage.
   */
  scorecardId?: string | null;
  /** Per-criterion grades keyed by criterion name (0 fail / 50 partial / 100 pass). */
  criterionScores?: Record<string, number>;
}

const reviewsRoot = ['messaging', 'iris-reviews'] as const;
export const irisReviewsKey = (status?: ReviewStatus) =>
  [...reviewsRoot, 'list', status ?? 'ALL'] as const;
export const irisReviewCountsKey = [...reviewsRoot, 'counts'] as const;

/** List conversations in the review queue, optionally filtered by lifecycle
 * status. `status` omitted = all. */
/**
 * Normalize a raw backend review row to {@link IrisReviewRow}. ai-operations
 * persists Prisma fields (`passed`, `reviewerId`, `updatedAt`) and the proxy
 * forwards them raw — map them to the display shape the UI reads (`verdict`,
 * `reviewedBy`, `reviewedAt`) so already-graded rows render Pass/Fail correctly.
 */
function normalizeReviewRow(raw: Record<string, unknown>): IrisReviewRow {
  const passed = raw.passed as boolean | null | undefined;
  return {
    ...(raw as unknown as IrisReviewRow),
    verdict:
      raw.verdict !== undefined
        ? (raw.verdict as ReviewVerdict | null)
        : passed == null
          ? null
          : passed
            ? 'PASS'
            : 'FAIL',
    reviewedBy: (raw.reviewedBy ?? raw.reviewerId ?? null) as string | null,
    reviewedAt: (raw.reviewedAt ?? raw.updatedAt ?? null) as string | null,
    scorecardId: (raw.scorecardId ?? null) as string | null,
    // Persisted as JSON, so it can come back as null or a non-object.
    criterionScores:
      raw.criterionScores && typeof raw.criterionScores === 'object'
        ? (raw.criterionScores as Record<string, number>)
        : null,
  };
}

export function useReviews(status?: ReviewStatus) {
  return useQuery({
    queryKey: irisReviewsKey(status),
    queryFn: async () => {
      const qs = status ? `?status=${status}` : '';
      const list = await apiGet<{ items?: Record<string, unknown>[] } & IrisReviewList>(
        operationsClient,
        `${BASE}${qs}`,
      );
      return { ...list, items: (list.items ?? []).map(normalizeReviewRow) } as IrisReviewList;
    },
    staleTime: 15_000,
  });
}

/** Counts per lifecycle bucket (Not reviewed / Reviewed / Needs fix / Fix
 * complete) for the filter-chip badges. */
export function useReviewCounts() {
  return useQuery({
    queryKey: irisReviewCountsKey,
    queryFn: () => apiGet<ReviewCounts>(operationsClient, `${BASE}/counts`),
    staleTime: 15_000,
  });
}

/** Pull a fresh sample of terminal conversations into the review queue as
 * NOT_REVIEWED items to grade. */
export function useAutosampleReviews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (limit?: number) =>
      apiPost<{ created: number }, { limit?: number }>(
        operationsClient,
        `${BASE}/autosample`,
        limit ? { limit } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reviewsRoot });
    },
  });
}

/** Create or update a review grade for a conversation. */
export function useUpsertReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, verdict, ...rest }: UpsertReviewPayload) =>
      // PUT keyed on the conversation id (path); the backend persists `passed`
      // (boolean), not `verdict`, and rejects undeclared fields — so map
      // PASS/FAIL → passed and drop verdict.
      apiPut<Record<string, unknown>, Record<string, unknown>>(
        operationsClient,
        `${BASE}/${encodeURIComponent(conversationId)}`,
        {
          ...rest,
          ...(verdict !== undefined ? { passed: verdict === 'PASS' } : {}),
        },
      ).then(normalizeReviewRow),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reviewsRoot });
    },
  });
}
